import { parseAbsolute } from '@internationalized/date';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DateSegment,
  Dialog,
  FieldError,
  Group,
  Heading,
  Label,
  Popover,
  DatePicker as ReactAriaDatePicker,
  Text,
} from 'react-aria-components';
import { HelpIcon } from '../molecules';

type Props = {
  additionalClassNames?: string;
  label?: string;
  includeTime?: boolean;
  date: Date | null;
  setDate: (date: Date) => void;
  description?: string;
  timezone?: string;
  maxDate?: Date;
  minDate?: Date;
  helpTextHeader?: string;
  helpTextBody?: string;
  required?: boolean;
  errorText?: string;
};

export const DatePickerWithTimezone = ({
  label,
  includeTime,
  date,
  timezone,
  setDate,
  description,
  minDate,
  maxDate,
  additionalClassNames,
  helpTextHeader,
  helpTextBody,
  required,
  errorText,
}: Props) => {
  const dateInTimezone = date ? parseAbsolute(date.toISOString(), timezone ?? 'Etc/UTC') : null;
  const minDateInTimezone =
    minDate ? parseAbsolute(minDate.toISOString(), timezone ?? 'Etc/UTC') : undefined;
  const maxDateInTimezone =
    maxDate ? parseAbsolute(maxDate.toISOString(), timezone ?? 'Etc/UTC') : undefined;

  const hasHelpText = helpTextHeader || helpTextBody;

  return (
    <div className='mw-datepicker'>
      <div>
        <ReactAriaDatePicker
          onChange={(e) => {
            if (e) {
              setDate(e.toDate());
            }
          }}
          value={dateInTimezone}
          granularity={includeTime ? 'minute' : 'day'}
          className={`group flex flex-col gap-1 ${includeTime ? 'w-[320px]' : 'w-[200px'} ${additionalClassNames}`}
          minValue={minDateInTimezone}
          maxValue={maxDateInTimezone}
          isRequired={required}
          isInvalid={!!errorText}
          // let react-hook-form handle error message/validation fail
          validationBehavior='aria'
        >
          <Label>{label}</Label>
          <Group className='flex rounded-lg bg-white/90 focus-within:bg-white group-open:bg-white transition pl-3 shadow-md text-gray-700 focus-visible:ring-2 ring-black border'>
            <DateInput className='flex flex-1 py-2 pr-2'>
              {(segment) => (
                <DateSegment
                  segment={segment}
                  className='px-0.5 tabular-nums outline-none rounded-sm focus:bg-gray-100 caret-transparent placeholder-shown:italic'
                />
              )}
            </DateInput>
            <Button className='outline-none px-1 pr-2 flex items-center text-gray-700 transition border-0 border-solid bg-transparent rounded-r-lg pressed:bg-purple-100 focus-visible:ring-2 ring-black'>
              ▼
            </Button>
          </Group>
          {description && <Text slot='description'>{description}</Text>}
          {/* Rendered inside the DatePicker so react-aria wires it up via aria-describedby */}
          <FieldError className='text-sm text-red-600'>{errorText}</FieldError>
          {hasHelpText && (
            // This wrapping div creates padding to houes the help icon which appears above the input
            <div className='flex ml-[-40px] z-10'>
              <HelpIcon header={helpTextHeader} supportingText={helpTextBody} />
            </div>
          )}
          <Popover className='overflow-auto rounded-lg drop-shadow-lg ring-1 ring-black/10 bg-gray-100 border'>
            <Dialog className='p-6 text-black'>
              <Calendar>
                <div className='flex items-center gap-1 pb-4 px-1 font-serif w-full'>
                  <Button slot='previous'>◀</Button>
                  <Heading className='flex-1 font-semibold text-2xl ml-2' />
                  <Button slot='next'>▶</Button>
                </div>
                <CalendarGrid className='border-spacing-1 border-separate'>
                  {(date) => (
                    <CalendarCell
                      date={date}
                      className='w-9 h-9 outline-none cursor-default rounded-full flex items-center justify-center outside-month:text-gray-300 hover:bg-gray-100 pressed:bg-white selected:bg-violet-700 selected:text-white focus-visible:ring ring-violet-600/70 ring-offset-2'
                    />
                  )}
                </CalendarGrid>
              </Calendar>
            </Dialog>
          </Popover>
        </ReactAriaDatePicker>
      </div>
    </div>
  );
};
